import React, { useEffect, useMemo, useState } from "react";
import {
  fetchFeaturedFixtures,
  fetchSocialAdminMonitor,
  syncAdminExternalMedia,
  syncAdminHighlights,
} from "../api";

const DEBUG_ADMIN_SOCIAL_MONITOR = process.env.NODE_ENV !== "production";

const cardStyle = {
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(180deg, rgba(8,20,17,0.96), rgba(6,13,12,0.96))",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
};

const chipStyle = (active) => ({
  padding: "6px 12px",
  borderRadius: 999,
  border: active
    ? "1px solid rgba(49,212,139,0.7)"
    : "1px solid rgba(148,163,184,0.28)",
  background: active ? "rgba(22,185,107,0.18)" : "rgba(255,255,255,0.05)",
  color: "#e8f5ec",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
});

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const formatFixtureLabel = (row = {}) => {
  const home = firstDefined(row?.home_team, row?.fixture?.home_team, row?.home, row?.fixture_home_team);
  const away = firstDefined(row?.away_team, row?.fixture?.away_team, row?.away, row?.fixture_away_team);
  if (home || away) return `${home || "Home"} vs ${away || "Away"}`;
  return firstDefined(row?.matchup, row?.fixture_label, row?.target_label, "Unknown fixture");
};

const getInternalFixtureId = (row = {}) =>
  firstDefined(row?.fixture_id, row?.fixture?.fixture_id, row?.fixture?.id, row?.id, row?.target_id);

const mergeFixtureMetric = (fixtureMap, row, metricKey, metricValue) => {
  const fixtureId = getInternalFixtureId(row);
  if (!fixtureId) return;
  const key = String(fixtureId);
  const existing = fixtureMap.get(key) || {
    ...row,
    fixture_id: fixtureId,
    comments: 0,
    poll_votes: 0,
    motm_votes: 0,
    likes: 0,
  };
  fixtureMap.set(key, {
    ...existing,
    ...row,
    fixture_id: fixtureId,
    comments: existing.comments,
    poll_votes: existing.poll_votes,
    motm_votes: existing.motm_votes,
    likes: existing.likes,
    [metricKey]: toNumber(metricValue),
  });
};

const mergeMonitorFixtureRows = (payload = {}) => {
  const fixtureMap = new Map();

  toArray(payload?.top_commented_fixtures).forEach((row) => {
    mergeFixtureMetric(
      fixtureMap,
      row,
      "comments",
      firstDefined(row?.comments, row?.comments_total, row?.comment_count, row?.count, 0)
    );
  });
  toArray(payload?.top_poll_fixtures).forEach((row) => {
    mergeFixtureMetric(
      fixtureMap,
      row,
      "poll_votes",
      firstDefined(row?.votes, row?.poll_votes, row?.poll_votes_total, row?.votes_total, row?.poll_total, 0)
    );
  });
  toArray(payload?.top_motm_fixtures).forEach((row) => {
    mergeFixtureMetric(
      fixtureMap,
      row,
      "motm_votes",
      firstDefined(row?.votes, row?.motm_votes, row?.motm_votes_total, row?.motm_total, 0)
    );
  });
  toArray(payload?.top_liked_fixtures).forEach((row) => {
    mergeFixtureMetric(
      fixtureMap,
      row,
      "likes",
      firstDefined(row?.likes, row?.fixture_likes, row?.fixture_likes_total, row?.likes_total, 0)
    );
  });

  return Array.from(fixtureMap.values()).sort((a, b) => {
    const totalA = toNumber(a.comments) + toNumber(a.poll_votes) + toNumber(a.motm_votes) + toNumber(a.likes);
    const totalB = toNumber(b.comments) + toNumber(b.poll_votes) + toNumber(b.motm_votes) + toNumber(b.likes);
    return totalB - totalA;
  });
};

const leaderboardAccuracy = (row) => {
  const explicit = firstDefined(row?.accuracy_pct, row?.accuracy, row?.win_rate_pct);
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    const value = Number(explicit);
    return Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";
  }
  const correct = Number(firstDefined(row?.correct_picks, row?.correct, row?.wins, 0));
  const total = Number(firstDefined(row?.predictions, row?.prediction_count, row?.total_predictions, 0));
  if (!total) return "—";
  return `${((correct / total) * 100).toFixed(1)}%`;
};

function SummaryCard({ label, value, hint }) {
  return (
    <div
      style={{
        border: "1px solid rgba(148,163,184,0.18)",
        borderRadius: 12,
        padding: 12,
        background: "rgba(255,255,255,0.03)",
      }}
    >
      <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: "#f8fafc" }}>{value}</div>
      {hint ? <div style={{ marginTop: 4, fontSize: 12, color: "#8aa094" }}>{hint}</div> : null}
    </div>
  );
}

function SyncResult({ result }) {
  if (!result) return null;
  const errors = Array.isArray(result.errors) ? result.errors : [];
  return (
    <div style={{ marginTop: 10, fontSize: 12, color: "#cbd5e1", display: "grid", gap: 4 }}>
      <div>
        Requests <strong>{firstDefined(result.requests, "—")}</strong> • Stored{" "}
        <strong>{firstDefined(result.stored, "—")}</strong> • Updated{" "}
        <strong>{firstDefined(result.updated, "—")}</strong> • Skipped{" "}
        <strong>{firstDefined(result.skipped_unmapped, "—")}</strong>
      </div>
      {errors.length ? <div style={{ color: "#fca5a5" }}>Errors: {errors.slice(0, 3).join("; ")}</div> : null}
    </div>
  );
}

function FixtureActivityRow({ row }) {
  const comments = toNumber(firstDefined(row?.comments, row?.comments_total, row?.comment_count, 0));
  const polls = toNumber(firstDefined(row?.poll_votes, row?.votes, row?.poll_votes_total, row?.votes_total, row?.poll_total, 0));
  const likes = toNumber(firstDefined(row?.likes, row?.fixture_likes, row?.fixture_likes_total, row?.likes_total, 0));
  const motm = toNumber(firstDefined(row?.motm_votes, row?.motm, row?.motm_votes_total, row?.motm_total, 0));

  return (
    <div
      style={{
        border: "1px solid rgba(148,163,184,0.16)",
        borderRadius: 12,
        padding: 12,
        background: "rgba(255,255,255,0.03)",
        display: "grid",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 800, color: "#f8fafc" }}>{formatFixtureLabel(row)}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
            {firstDefined(row?.comp, row?.competition, row?.fixture?.comp, "—")} • {formatTimestamp(firstDefined(row?.kickoff_utc, row?.fixture?.kickoff_utc))}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#9fdcb7", fontWeight: 800 }}>
          {comments + polls + likes + motm} interactions
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
        <span style={{ color: "#cbd5e1" }}>Comments <strong>{comments}</strong></span>
        <span style={{ color: "#cbd5e1" }}>Poll votes <strong>{polls}</strong></span>
        <span style={{ color: "#cbd5e1" }}>Likes <strong>{likes}</strong></span>
        <span style={{ color: "#cbd5e1" }}>MOTM <strong>{motm}</strong></span>
      </div>
    </div>
  );
}

export default function AdminSocialMonitoringPanel() {
  const [days, setDays] = useState(7);
  const [day, setDay] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [monitorPayload, setMonitorPayload] = useState(null);
  const [featuredPayload, setFeaturedPayload] = useState(null);
  const [syncing, setSyncing] = useState("");
  const [highlightSyncResult, setHighlightSyncResult] = useState(null);
  const [mediaSyncResult, setMediaSyncResult] = useState(null);
  const [syncError, setSyncError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      fetchSocialAdminMonitor({
        days,
        fixture_limit: 12,
        recent_comments_limit: 20,
        leaderboard_limit: 10,
      }),
      fetchFeaturedFixtures(day),
    ])
      .then(([monitorData, featuredData]) => {
        if (cancelled) return;
        setMonitorPayload(monitorData || null);
        setFeaturedPayload(featuredData || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err?.message || "Could not load social monitoring.");
        setMonitorPayload(null);
        setFeaturedPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [day, days]);

  const summary = useMemo(() => monitorPayload?.summary || {}, [monitorPayload]);
  const featuredFixtures = toArray(
    firstDefined(
      featuredPayload?.featured_fixtures,
      featuredPayload?.fixtures,
      featuredPayload?.featured,
      []
    )
  );
  const mergedFixtureRows = useMemo(() => mergeMonitorFixtureRows(monitorPayload), [monitorPayload]);
  const mergedFixtureById = useMemo(
    () => new Map(mergedFixtureRows.map((row) => [String(row.fixture_id), row])),
    [mergedFixtureRows]
  );
  const featuredActivityRows = useMemo(
    () =>
      featuredFixtures.map((row) => {
        const fixtureId = getInternalFixtureId(row);
        const metrics = fixtureId ? mergedFixtureById.get(String(fixtureId)) : null;
        return metrics ? { ...row, ...metrics, fixture_id: fixtureId } : row;
      }),
    [featuredFixtures, mergedFixtureById]
  );
  const leaderboard = toArray(
    firstDefined(
      featuredPayload?.prediction_leaderboard,
      featuredPayload?.leaderboard,
      featuredPayload?.predictions_leaderboard,
      []
    )
  );
  const recentComments = toArray(monitorPayload?.recent_comments);
  const topCommenters = toArray(monitorPayload?.top_commenters);
  const fanRooms = toArray(monitorPayload?.fan_rooms);
  const recentVotes = toArray(monitorPayload?.recent_votes);

  const activeFeaturedCount = useMemo(
    () =>
      featuredActivityRows.filter((row) => {
        const comments = toNumber(firstDefined(row?.comments, row?.comments_total, row?.comment_count, 0));
        const polls = toNumber(firstDefined(row?.poll_votes, row?.votes, row?.poll_votes_total, row?.votes_total, row?.poll_total, 0));
        const likes = toNumber(firstDefined(row?.likes, row?.fixture_likes, row?.fixture_likes_total, row?.likes_total, 0));
        const motm = toNumber(firstDefined(row?.motm_votes, row?.motm, row?.motm_votes_total, row?.motm_total, 0));
        return comments > 0 || polls > 0 || likes > 0 || motm > 0;
      }).length,
    [featuredActivityRows]
  );

  useEffect(() => {
    if (!DEBUG_ADMIN_SOCIAL_MONITOR || !monitorPayload) return;
    console.info("[AdminSocialMonitor] mapped summary", {
      comments: toNumber(summary.comments_total),
      pollVotes: toNumber(summary.poll_votes_total),
      likes: toNumber(summary.fixture_likes_total),
      motm: toNumber(summary.motm_votes_total),
    });
    console.info(
      "[AdminSocialMonitor] mapped fixture rows",
      mergedFixtureRows.map((row) => ({
        fixture_id: row.fixture_id,
        label: formatFixtureLabel(row),
        comments: toNumber(row.comments),
        pollVotes: toNumber(row.poll_votes),
        likes: toNumber(row.likes),
        motm: toNumber(row.motm_votes),
      }))
    );
  }, [mergedFixtureRows, monitorPayload, summary]);

  const runSync = async (type) => {
    setSyncing(type);
    setSyncError("");
    try {
      const result =
        type === "highlights"
          ? await syncAdminHighlights({ limit: 80, requests: 40 })
          : await syncAdminExternalMedia({ limit: 100, requests: 10 });
      if (type === "highlights") setHighlightSyncResult(result || {});
      else setMediaSyncResult(result || {});
    } catch (err) {
      setSyncError(err?.response?.data?.detail || err?.message || "Could not run media sync.");
    } finally {
      setSyncing("");
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Community Monitoring</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
              Track how the Featured 5 is driving picks, poll predictions, comments, and leaderboard movement.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {[7, 14, 30].map((value) => (
              <button key={value} type="button" onClick={() => setDays(value)} style={chipStyle(days === value)}>
                {value}d
              </button>
            ))}
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              style={{
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "rgba(248,250,252,0.96)",
                color: "#08130f",
                padding: "0 10px",
              }}
            />
          </div>
        </div>

        {error ? <div style={{ marginTop: 12, color: "#fca5a5", fontSize: 13 }}>{error}</div> : null}
        {loading && !monitorPayload ? <div style={{ marginTop: 12, color: "#94a3b8", fontSize: 13 }}>Loading social monitoring…</div> : null}

        <div style={{ marginTop: 14, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          <SummaryCard label="Comments" value={firstDefined(summary.comments_total, "—")} />
          <SummaryCard label="Poll Votes" value={firstDefined(summary.poll_votes_total, "—")} />
          <SummaryCard label="MOTM Votes" value={firstDefined(summary.motm_votes_total, "—")} />
          <SummaryCard label="Fixture Likes" value={firstDefined(summary.fixture_likes_total, "—")} />
          <SummaryCard label="Featured 5 Active" value={`${activeFeaturedCount}/${featuredFixtures.length || 5}`} hint="Featured fixtures with community activity" />
          <SummaryCard label="Prediction Leaderboard" value={leaderboard.length} hint="Users ranked from signed-in poll voting" />
          <SummaryCard label="Fan Rooms" value={firstDefined(summary.fan_rooms_total, fanRooms.length, "—")} />
          <SummaryCard label="Room Memberships" value={firstDefined(summary.fan_room_memberships_total, "—")} hint="People joined across team communities" />
        </div>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)" }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#34d399", textTransform: "uppercase", letterSpacing: 1 }}>
            Fan-room intelligence
          </div>
          <p style={{ margin: "5px 0 12px", color: "#94a3b8", fontSize: 13 }}>
            Every team room, its league, and exactly who has joined it.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {fanRooms.length ? fanRooms.map((room, idx) => (
              <div key={`${room.team_key || room.team_name}-${idx}`} style={{ border: "1px solid rgba(52,211,153,.2)", borderRadius: 12, padding: 12, background: "rgba(2,22,15,.72)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
                  <div>
                    <div style={{ color: "#f8fafc", fontWeight: 850 }}>{room.team_name || "Unknown team"}</div>
                    <div style={{ color: "#8ea79b", fontSize: 12, marginTop: 3 }}>{room.league_name || room.league_key || "Unknown league"} · {room.sport || "sport"}</div>
                  </div>
                  <span style={{ color: "#052e20", background: "#34d399", borderRadius: 999, padding: "4px 9px", fontWeight: 900, fontSize: 12 }}>{toNumber(room.member_count)} members</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                  {toArray(room.members).map((member) => (
                    <span key={`${room.team_key}-${member.user_id}`} title={`${member.email || "No email"} · joined ${formatTimestamp(member.joined_at)}`} style={{ border: "1px solid rgba(148,163,184,.2)", background: "rgba(255,255,255,.04)", borderRadius: 8, padding: "5px 8px", color: "#dbe8e0", fontSize: 12 }}>
                      @{member.username || member.display_name || `user-${member.user_id}`}
                    </span>
                  ))}
                </div>
              </div>
            )) : <div style={{ color: "#94a3b8", fontSize: 13 }}>No fan-room memberships yet.</div>}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#fbbf24", textTransform: "uppercase", letterSpacing: 1 }}>
            Live voting ledger
          </div>
          <p style={{ margin: "5px 0 12px", color: "#94a3b8", fontSize: 13 }}>
            The latest signed-in match and fan-room votes in this window.
          </p>
          <div style={{ display: "grid", gap: 7, maxHeight: 520, overflowY: "auto" }}>
            {recentVotes.length ? recentVotes.map((vote, idx) => (
              <div key={`${vote.vote_type}-${vote.vote_id}-${idx}`} style={{ borderLeft: "3px solid #fbbf24", padding: "8px 10px", background: "rgba(255,255,255,.035)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong style={{ color: "#f8fafc", fontSize: 13 }}>@{vote.username || vote.display_name || `user-${vote.user_id || "anon"}`}</strong>
                  <span style={{ color: "#71837a", fontSize: 11 }}>{formatTimestamp(vote.created_at)}</span>
                </div>
                <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 4 }}>
                  {vote.question || (vote.fixture ? formatFixtureLabel(vote.fixture) : vote.room?.team_name || "Fan room")}
                </div>
                <div style={{ color: "#fbbf24", fontSize: 12, fontWeight: 800, marginTop: 3 }}>{String(vote.choice || "—")}</div>
              </div>
            )) : <div style={{ color: "#94a3b8", fontSize: 13 }}>No votes in this range.</div>}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Media Cache Sync</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
              Refresh cached YouTube highlights and trusted external media posts for the web and mobile surfaces.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => runSync("highlights")}
              disabled={!!syncing}
              style={chipStyle(syncing === "highlights")}
            >
              {syncing === "highlights" ? "Syncing..." : "Sync YouTube Highlights"}
            </button>
            <button
              type="button"
              onClick={() => runSync("media")}
              disabled={!!syncing}
              style={chipStyle(syncing === "media")}
            >
              {syncing === "media" ? "Syncing..." : "Sync Media Watch"}
            </button>
          </div>
        </div>
        {syncError ? <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 13 }}>{syncError}</div> : null}
        <div style={{ marginTop: 10, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <div style={{ border: "1px solid rgba(148,163,184,0.16)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.03)" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>YouTube Highlights</div>
            <SyncResult result={highlightSyncResult} />
          </div>
          <div style={{ border: "1px solid rgba(148,163,184,0.16)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.03)" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>Media Watch</div>
            <SyncResult result={mediaSyncResult} />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)" }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Featured 5 Activity
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {featuredActivityRows.length ? (
              featuredActivityRows.map((row, idx) => (
                <FixtureActivityRow key={`featured-fixture-${idx}`} row={row} />
              ))
            ) : (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No Featured 5 data returned for this day.</div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Prediction Leaderboard
          </div>
          <div style={{ marginTop: 12, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Rank", "User", "Points", "Accuracy", "Correct / Predictions"].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        textAlign: heading === "User" ? "left" : "right",
                        padding: "0 0 8px",
                        color: "#94a3b8",
                        borderBottom: "1px solid rgba(148,163,184,0.18)",
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaderboard.length ? (
                  leaderboard.map((row, idx) => {
                    const correct = firstDefined(row?.correct_picks, row?.correct, row?.wins, 0);
                    const total = firstDefined(row?.predictions, row?.prediction_count, row?.total_predictions, 0);
                    return (
                      <tr key={`leader-${idx}`} style={{ borderTop: "1px solid rgba(148,163,184,0.12)" }}>
                        <td style={{ textAlign: "right", padding: "10px 0" }}>{firstDefined(row?.rank, idx + 1)}</td>
                        <td style={{ textAlign: "left", padding: "10px 0" }}>
                          {firstDefined(row?.username, row?.display_name, row?.user_name, row?.user_id, "Unknown user")}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 0" }}>{firstDefined(row?.points, row?.score, 0)}</td>
                        <td style={{ textAlign: "right", padding: "10px 0" }}>{leaderboardAccuracy(row)}</td>
                        <td style={{ textAlign: "right", padding: "10px 0" }}>{correct} / {total}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: "12px 0", color: "#94a3b8" }}>
                      No prediction leaderboard rows returned for this day.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Top Fixture Activity
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {mergedFixtureRows.length ? (
              mergedFixtureRows.map((row, idx) => <FixtureActivityRow key={`activity-fixture-${row.fixture_id || idx}`} row={row} />)
            ) : (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No fixture activity in this range.</div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Monitor Source
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 8, fontSize: 13, color: "#cbd5e1" }}>
            <div>Endpoint: <strong>/api/social/admin/monitor</strong></div>
            <div>Window: <strong>{days} days</strong></div>
            <div>Fixture limit: <strong>12</strong></div>
            <div>Recent comments: <strong>20</strong></div>
            <div>Leaderboard limit: <strong>10</strong></div>
            <div style={{ color: "#94a3b8" }}>
              Fixture cards are merged by internal fixture_id from comments, poll, MOTM, and likes arrays.
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1.5fr) minmax(280px, 1fr)" }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Recent Comments
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {recentComments.length ? (
              recentComments.map((row, idx) => (
                <div
                  key={`recent-comment-${idx}`}
                  style={{
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 12,
                    padding: 12,
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13, color: "#f8fafc", fontWeight: 700 }}>
                      {firstDefined(row?.username, row?.display_name, row?.user_name, row?.user_id, "Unknown user")}
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      {formatTimestamp(firstDefined(row?.created_at, row?.timestamp))}
                    </div>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#cbd5e1", lineHeight: 1.45 }}>
                    {firstDefined(row?.body, row?.comment, row?.text, "—")}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
                    {firstDefined(row?.target_type, row?.comment_target_type, "fixture")} • {formatFixtureLabel(row)}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No recent comments in this range.</div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Top Commenters
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {topCommenters.length ? (
              topCommenters.map((row, idx) => (
                <div
                  key={`commenter-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 12,
                    padding: 12,
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#f8fafc" }}>
                      {firstDefined(row?.username, row?.display_name, row?.user_name, row?.user_id, `User ${idx + 1}`)}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>
                      {firstDefined(row?.target_types, row?.summary, "Community activity")}
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#86efac" }}>
                    {firstDefined(row?.comments_total, row?.comment_count, row?.count, 0)}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No commenter data in this range.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
